
import React from 'react';
import { Ticket, TicketStatus, TicketPriority, AppSettings, Employee, RepairChecklist } from '../types';
import { TicketBoardCard } from './TicketBoardCard';

interface TicketBoardPageProps {
  tickets: Ticket[];
  allStatuses: TicketStatus[]; 
  onTicketDropUpdateStatus: (ticketId: string, newStatus: TicketStatus) => void;
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile: (customerName: string) => void;
  appSettings: AppSettings;
  currentUser: Employee | null;
  repairChecklists: RepairChecklist[]; // New prop
  onUpdateTicketChecklistProgress: (ticketId: string, checklistItemId: string, completed: boolean) => void; // New prop
}

const BoardIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" />
  </svg>
);


export const TicketBoardPage: React.FC<TicketBoardPageProps> = ({
  tickets,
  allStatuses,
  onTicketDropUpdateStatus,
  onOpenUpdateStatusModal,
  onViewCustomerProfile,
  appSettings,
  currentUser,
  repairChecklists, // Destructure new prop
  onUpdateTicketChecklistProgress, // Destructure new prop
}) => {
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, ticketId: string) => {
    e.dataTransfer.setData('ticketId', ticketId);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); 
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, newStatus: TicketStatus) => {
    e.preventDefault();
    const ticketId = e.dataTransfer.getData('ticketId');
    if (ticketId) {
      const ticket = tickets.find(t => t.id === ticketId);
      if (ticket && ticket.status !== newStatus) {
        onTicketDropUpdateStatus(ticketId, newStatus);
      }
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white p-4 sm:p-6 rounded-t-lg shadow-md mb-0 sticky top-0 z-10">
        <div className="flex items-center">
          <BoardIcon />
          <h1 className="text-2xl font-bold text-[var(--color-primary)]">Ticket Board</h1>
        </div>
        <p className="text-sm text-slate-600">Drag and drop tickets to update their status.</p>
      </div>
      <div className="flex-grow flex space-x-4 p-4 overflow-x-auto bg-slate-100 rounded-b-lg">
        {allStatuses.map((status) => {
          const ticketsInStatus = tickets.filter(ticket => ticket.status === status);
          return (
            <div
              key={status}
              className="bg-slate-200 rounded-lg p-3 w-80 min-w-[300px] flex-shrink-0 shadow-sm"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
              aria-label={`Column for ${status} tickets`}
            >
              <h2 className="text-md font-semibold text-slate-700 mb-3 px-1 border-b border-slate-300 pb-2">
                {status} ({ticketsInStatus.length})
              </h2>
              <div className="space-y-3 min-h-[200px] max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
                {ticketsInStatus.map(ticket => (
                  <TicketBoardCard
                    key={ticket.id}
                    ticket={ticket}
                    appSettings={appSettings}
                    currentUser={currentUser}
                    onDragStart={(e) => handleDragStart(e, ticket.id)}
                    onOpenUpdateStatusModal={onOpenUpdateStatusModal} 
                    onViewCustomerProfile={onViewCustomerProfile}
                    repairChecklists={repairChecklists} // Pass down
                    onUpdateTicketChecklistProgress={onUpdateTicketChecklistProgress} // Pass down
                  />
                ))}
                {ticketsInStatus.length === 0 && (
                  <p className="text-xs text-slate-500 text-center py-4">No tickets in this status.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
